import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { useState } from 'react';
import { CommonDialog, CommonDialogHeader } from '..';
import { useForm } from 'react-hook-form';
import { ICreatePermissionRequest } from '../../../types/api/request/permissionsrequest';
import { PermissionsService } from '../../../api/services/permissionsService';
import { useDispatch, useSelector } from 'react-redux';
import { addPermission } from '../../../store/Reducers/PermissionsSlice';
import { IRootState } from '../../../types/store';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IAddNewPermissionDialogBodyProps } from '../../../types/dialog';

export const AddNewPermissionDialog = () => {
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const dispatch = useDispatch();

  const onSubmit = async (payload: ICreatePermissionRequest) => {
    try {
      const permission = await PermissionsService.createPermission(payload);
      dispatch(addPermission(permission));
      dispatch(
        showNotification({
          message: 'Permission successfully created',
        }),
      );
      setIsOpenDialog(false);
    } catch (error: any) {
      dispatch(
        showNotification({
          message: `Failed to add new permission. ${error?.response?.data?.message || ''}`,
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}
        onClick={() => {
          setIsOpenDialog(true);
        }}
      >
        Add New Permission
      </Button>
      {isOpenDialog ? (
        <CommonDialog
          isOpen={isOpenDialog}
          header={
            <CommonDialogHeader
              title={'Add New Permission'}
              onClose={() => {
                setIsOpenDialog(false);
              }}
            />
          }
          body={<DialogBody onSubmit={onSubmit} />}
          footer={undefined}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const DialogBody = ({ onSubmit }: IAddNewPermissionDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ICreatePermissionRequest>({
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const { content: permissions } = useSelector((state: IRootState) => state.permissions);

  const handleFormSubmit = async (data: ICreatePermissionRequest) => {
    try {
      await onSubmit(data); // call parent onSubmit
      reset(); // reset form fields after success
    } catch {}
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        {/* Username */}
        <FormControl>
          <FormLabel>Name</FormLabel>
          <TextField
            type="text"
            placeholder="Permission 01"
            fullWidth
            variant="outlined"
            error={!!errors.name}
            helperText={errors.name?.message}
            {...register('name', {
              required: 'Permission name is required',
              validate: (value) => {
                const nameExists = permissions?.some(
                  (p) => p.name.toLowerCase() === value.trim().toLowerCase(),
                );
                return nameExists ? 'Permission already exists' : true;
              },
            })}
          />
        </FormControl>

        {/* Password */}
        <FormControl>
          <FormLabel>Description</FormLabel>
          <TextField
            type="text"
            placeholder="This permission is for..."
            fullWidth
            variant="outlined"
            error={!!errors.description}
            helperText={errors.description?.message}
            {...register('description', {
              required: 'Description is required',
            })}
          />
        </FormControl>

        {/* Submit */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ backgroundColor: '#000' }}
        >
          {isSubmitting ? 'Creating...' : 'Create'}
        </Button>
      </Box>
    </>
  );
};
